// i18n-ns: budget (the hook returns label/hint KEYS; the field component resolves them through its own t)
import { useCallback, useMemo, useState } from 'react';
import {
    BudgetScenarioPartnerLocationOption,
    BudgetScenarioType,
    BudgetScenarioWorkingUnitOption,
    BudgetScenarioWorktypeOption,
} from '@callmart/types';
import {
    ScenarioInputsDraft,
    driverRowHasValues,
    entityRowHasValues,
    newContactsDraftEntity,
    newDriverDraftRow,
    newEntityDraftRow,
    newShiftDraftRow,
    shiftRowHasInput,
} from './scenarioDraft';

/** One pickable entity of the scope multiselect. `id` is what CustomAutoComplete keys options by. */
export interface ScenarioScopeOption {
    id: number;
    label: string;
}

/** What a type's multiselect picks. Shift picks Working Units (worktype – channel – task): a Verschiebung
 *  moves volume within one of them, which is finer than a worktype (CALL-783). */
type ScopeEntity = 'worktype' | 'partnerLocation' | 'workingUnit';

interface UseScenarioScopeArgs {
    type: BudgetScenarioType | undefined;
    /** The active type's draft. The selection is a projection of it, so form-level Cancel reverts both. */
    inputs: ScenarioInputsDraft | null;
    onChange: (next: ScenarioInputsDraft) => void;
    availableWorktypes: BudgetScenarioWorktypeOption[];
    availablePartnerLocations: BudgetScenarioPartnerLocationOption[];
    availableWorkingUnits: BudgetScenarioWorkingUnitOption[];
    /** The scenario's start date ('YYYY-MM-DD') — what a new contacts/driver row's Start Training is
     *  prefilled with (CALL-801). NOT the shift row's: a prefilled date would count as input there
     *  (shiftRowHasInput), breaking the pristine-row handling of ADD SHIFT. */
    scenarioStart?: string;
}

export interface ScenarioScope {
    /** null for a type with no scope of its own (or none loaded yet) — the field renders nothing. */
    entity: ScopeEntity | null;
    labelKey: string;
    hintKey: string;
    /** The bucket's entities, plus any selected id the bucket no longer offers (see below). */
    options: ScenarioScopeOption[];
    selectedIds: number[];
    selected: ScenarioScopeOption[];
    select: (nextIds: number[]) => void;
    /** The entities of the requested selection whose removal would discard entered values, awaiting one
     *  confirmation. Empty when nothing is pending. Plural because ONE gesture can drop several: the
     *  multiselect's clear (X) asks for `[]` in a single onChange. */
    pendingRemovals: ScenarioScopeOption[];
    confirmRemoval: () => void;
    cancelRemoval: () => void;
}

const unique = (ids: number[]): number[] => [...new Set(ids)];

// Per-type vocabulary: what the multiselect picks and how it explains itself. One table rather than five
// branches spread across the field, the page and the grids.
//
// Every type's selector sits in the same slot, in the metadata form above the section heading. Budget
// adjustment used to be placed beside the impact table instead, on the reasoning that its worktype list
// scopes the € → contacts CONVERSION rather than the euro series — which is true of the arithmetic and
// wrong for the user: the field was then absent from the very tab where the euros are entered, and on the
// impact tab it rendered UNDER the table it scopes. A control that filters something belongs above it, and
// one control belongs in one place across five screens.
const SCOPE_BY_TYPE: Record<BudgetScenarioType, { entity: ScopeEntity; labelKey: string; hintKey: string }> = {
    [BudgetScenarioType.CONTACTS]: {
        entity: 'worktype',
        labelKey: 'scenarioDetail.scopeWorktypes',
        hintKey: 'hint.contactsScope',
    },
    [BudgetScenarioType.MINUTE_PRICE]: {
        entity: 'partnerLocation',
        labelKey: 'scenarioDetail.scopePartnerLocations',
        hintKey: 'hint.minutePriceScope',
    },
    [BudgetScenarioType.SHIFT]: {
        entity: 'workingUnit',
        labelKey: 'scenarioDetail.scopeWorkingUnits',
        hintKey: 'hint.shiftScope',
    },
    [BudgetScenarioType.BUDGET_ADJUSTMENT]: {
        entity: 'worktype',
        labelKey: 'scenarioDetail.scopeWorktypes',
        // Keeps its own wording: this is the one scope where an EMPTY selection means "all worktypes"
        // rather than "no rows", because it scopes a conversion and not a row set.
        hintKey: 'hint.budgetAdjustmentScope',
    },
    [BudgetScenarioType.REACHABILITY_ADJUSTMENT]: {
        entity: 'worktype',
        labelKey: 'scenarioDetail.scopeWorktypes',
        hintKey: 'hint.driverScope',
    },
};

/** The ids a draft currently holds — the selection as the DATA sees it. */
const draftScopeIds = (inputs: ScenarioInputsDraft): number[] => {
    switch (inputs.type) {
        case BudgetScenarioType.CONTACTS:
        case BudgetScenarioType.MINUTE_PRICE:
            return unique(inputs.entities.map((entity) => entity.entityId));
        case BudgetScenarioType.SHIFT:
            return unique(inputs.rows.map((row) => row.workingUnitId).filter((id): id is number => id !== ''));
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return unique(inputs.rows.map((row) => row.worktypeId).filter((id): id is number => id !== ''));
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            return unique(inputs.worktypeIds);
    }
};

/** The label a draft remembers for an id, so an entity the bucket no longer offers still reads as a name. */
const draftScopeLabel = (inputs: ScenarioInputsDraft, id: number): string | null => {
    switch (inputs.type) {
        case BudgetScenarioType.CONTACTS:
        case BudgetScenarioType.MINUTE_PRICE:
            return inputs.entities.find((entity) => entity.entityId === id)?.entityLabel ?? null;
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return inputs.rows.find((row) => row.worktypeId === id)?.worktypeName ?? null;
        case BudgetScenarioType.SHIFT:
            return inputs.rows.find((row) => row.workingUnitId === id)?.workingUnitLabel ?? null;
        // The budget-adjustment scope persists bare worktype ids, with no label to fall back on — those
        // orphans show as "#id", which is still selectable and therefore removable.
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            return null;
    }
};

/** True when dropping `id` from the selection would discard values the user entered. */
const scopeIdHasValues = (inputs: ScenarioInputsDraft, id: number): boolean => {
    switch (inputs.type) {
        case BudgetScenarioType.CONTACTS:
        case BudgetScenarioType.MINUTE_PRICE:
            // For Contacts that includes the training scalars, not just the monthly deltas — entering
            // "10 agents, 24 days" and nothing else is still work a deselect would throw away.
            return inputs.entities.some((entity) => entity.entityId === id && entityRowHasValues(entity));
        case BudgetScenarioType.SHIFT:
            return inputs.rows.some((row) => row.workingUnitId === id && shiftRowHasInput(row));
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return inputs.rows.some((row) => row.worktypeId === id && driverRowHasValues(row));
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            // The euro series is not per worktype — narrowing the conversion scope discards nothing.
            return false;
    }
};

/** Apply a new selection to the draft: rows for added ids are created, rows whose entity was deselected go.
 *  Every type projects its selection into the draft this way, which is what makes the selection derivable
 *  from it — see the hook's own note on Shift, the one type where the two are not a bijection. */
const applyScope = (
    inputs: ScenarioInputsDraft,
    nextIds: number[],
    labelFor: (id: number) => string | null,
    scenarioStart?: string
): ScenarioInputsDraft => {
    switch (inputs.type) {
        // Contacts and Minute-price differ only in what a new row starts as: a contacts row also carries the
        // worktype's training block, which is created zeroed rather than absent (CALL-783 Stage 4).
        case BudgetScenarioType.CONTACTS: {
            const kept = inputs.entities.filter((entity) => nextIds.includes(entity.entityId));
            const added = nextIds
                .filter((id) => !inputs.entities.some((entity) => entity.entityId === id))
                .map((id) => newContactsDraftEntity(id, labelFor(id), scenarioStart));
            return { ...inputs, entities: [...kept, ...added] };
        }
        case BudgetScenarioType.MINUTE_PRICE: {
            const kept = inputs.entities.filter((entity) => nextIds.includes(entity.entityId));
            const added = nextIds
                .filter((id) => !inputs.entities.some((entity) => entity.entityId === id))
                .map((id) => newEntityDraftRow(id, labelFor(id)));
            return { ...inputs, entities: [...kept, ...added] };
        }
        case BudgetScenarioType.SHIFT: {
            // Picking a working unit creates its FIRST row, with Von/Nach still empty — those depend on the
            // unit (only the locations it runs at can be priced), so the pair is chosen in the grid. Further
            // targets for the same unit come from the grid's ADD SHIFT, hence "first" rather than "the" row:
            // a unit with two rows is still one selection entry, and stays selected when one of them goes.
            //
            // A row whose working unit is still unpicked belongs to no scope entry — it was just added with
            // that button, and must survive every selection change until it is filled in.
            const kept = inputs.rows.filter((row) => row.workingUnitId === '' || nextIds.includes(row.workingUnitId));
            const added = nextIds
                .filter((id) => !inputs.rows.some((row) => row.workingUnitId === id))
                .map((id) => newShiftDraftRow(id, labelFor(id)));
            return { ...inputs, rows: [...kept, ...added] };
        }
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT: {
            const kept = inputs.rows.filter((row) => row.worktypeId === '' || nextIds.includes(row.worktypeId));
            const added = nextIds
                .filter((id) => !inputs.rows.some((row) => row.worktypeId === id))
                .map((id) => newDriverDraftRow(id, labelFor(id), scenarioStart));
            return { ...inputs, rows: [...kept, ...added] };
        }
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            return { ...inputs, worktypeIds: nextIds };
    }
};

// The scope multiselect behind every type's screen (CALL-783). The board makes the entity choice the focal
// control at the top of the form, replacing the per-grid "ADD WORKTYPE" button + dialog; this hook is the
// single place that decides what that choice means for each type.
//
// The selection is DERIVED from the draft rather than stored beside it, so the form's Cancel reverts both at
// once and the two can never disagree — there is no "picked but not represented" state to keep in sync,
// because every pick creates something the draft can be read back from. Shift used to be the exception (its
// pick created nothing, so the id had to be remembered locally); it now creates its first row like the rest,
// and is merely not a BIJECTION with the rows — a working unit moving to two targets is one selected entry
// with two rows, and stays selected while either of them lives.
export const useScenarioScope = ({
    type,
    inputs,
    onChange,
    availableWorktypes,
    availablePartnerLocations,
    availableWorkingUnits,
    scenarioStart,
}: UseScenarioScopeArgs): ScenarioScope => {
    // A confirmation holds the WHOLE requested selection, not just the ids that need confirming: what the
    // user asked for is the only thing that can be applied faithfully, and re-deriving the commit from the
    // pending ids alone is what made the multiselect's clear (X) remove exactly one of several entities.
    const [pending, setPending] = useState<{ removedIds: number[]; nextIds: number[] } | null>(null);

    const config = type ? SCOPE_BY_TYPE[type] : undefined;

    const available = useMemo<ScenarioScopeOption[]>(() => {
        if (config?.entity === 'partnerLocation') {
            return availablePartnerLocations.map((option) => ({ id: option.locationId, label: option.label }));
        }
        if (config?.entity === 'workingUnit') {
            return availableWorkingUnits.map((option) => ({ id: option.workingUnitId, label: option.label }));
        }
        if (config?.entity === 'worktype') {
            return availableWorktypes.map((option) => ({ id: option.worktypeId, label: option.worktypeName }));
        }
        return [];
    }, [config?.entity, availableWorktypes, availablePartnerLocations, availableWorkingUnits]);

    const selectedIds = useMemo(() => (inputs ? draftScopeIds(inputs) : []), [inputs]);

    // Orphan defence: a selected id the bucket no longer offers (the scenario was re-pointed at another
    // version, or a billing config went away) must still appear as an option — MUI drops a value that is
    // not among the options, and its row would then be unremovable.
    const options = useMemo(() => {
        if (!inputs) return available;
        const orphans = selectedIds
            .filter((id) => !available.some((option) => option.id === id))
            .map((id) => ({ id, label: draftScopeLabel(inputs, id) ?? `#${id}` }));
        return [...available, ...orphans];
    }, [available, selectedIds, inputs]);

    const selected = useMemo(() => options.filter((option) => selectedIds.includes(option.id)), [options, selectedIds]);

    const commit = useCallback(
        (nextIds: number[]) => {
            if (!inputs) return;
            const labelFor = (id: number) =>
                available.find((option) => option.id === id)?.label ?? draftScopeLabel(inputs, id);
            // Reporting the new draft upward is the whole commit: the next render reads the selection back
            // out of it, so there is nothing else to keep in step (and nothing that could outlive a Cancel).
            onChange(applyScope(inputs, nextIds, labelFor, scenarioStart));
        },
        [inputs, available, onChange, scenarioStart]
    );

    const select = useCallback(
        (nextIds: number[]) => {
            if (!inputs) return;
            // A removal that throws away entered values is confirmed first; everything else (additions, and
            // dropping an entity that is still empty) applies straight away.
            //
            // Several destructive removals in one event are confirmed TOGETHER rather than queued: the user
            // made one gesture, so one prompt naming all of them is what matches it — and the commit stays
            // atomic. Asking N times for one click would also let a Cancel halfway through leave a selection
            // the user never requested, which is the state the whole guard exists to prevent.
            const removed = selectedIds.filter((id) => !nextIds.includes(id));
            const destructive = removed.filter((id) => scopeIdHasValues(inputs, id));
            if (destructive.length > 0) {
                setPending({ removedIds: destructive, nextIds });
                return;
            }
            commit(nextIds);
        },
        [inputs, selectedIds, commit]
    );

    // Commits exactly what select() was asked for — the empty selection of a clear included, not one id less.
    const confirmRemoval = useCallback(() => {
        if (!pending) return;
        commit(pending.nextIds);
        setPending(null);
    }, [pending, commit]);

    const cancelRemoval = useCallback(() => setPending(null), []);

    // In the selection's own order, so the dialog names them the way the field lists them.
    const pendingRemovals = useMemo(
        () =>
            pending
                ? options.filter((option) => pending.removedIds.includes(option.id))
                : ([] as ScenarioScopeOption[]),
        [pending, options]
    );

    return {
        entity: config && inputs ? config.entity : null,
        labelKey: config?.labelKey ?? '',
        hintKey: config?.hintKey ?? '',
        options,
        selectedIds,
        selected,
        select,
        pendingRemovals,
        confirmRemoval,
        cancelRemoval,
    };
};
